import React, { useState, useMemo, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import "./style.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import UploadFrfData from "./pages/UploadFrfData/UploadData";
import ReportAnEvent from "./pages/ReportAnEvent/index";
import SelectModes from "./pages/SelectModes";
import useAPI from "effects/useAPI";
import getMaintenanceFrfDataByMachineId from "services/frfDatabase/getMaintenanceFrfDataByMachineId";
import deleteMaintenanceFrfDataById from "services/frfDatabase/deleteMaintenanceFrfDataById";
import notify from "utils/toastMsg";
import GetFileAsJson from "components/GetFileAsJson";
import getMachineByMachineId from "services/getMachineByMachineId";
import getEventLogsByMachineId from "services/getEventLogsByMachineId";
import DeleteFrfRecordById from "./components/DeleteFrfRecordById";

const calculateMagnitude = (real, imag) => {
  return Math.sqrt(Math.pow(real, 2) + Math.pow(imag, 2));
};

const parseData = (dataStringX, dataStringY, keyPrefix = "") => {
  if (!dataStringX || !dataStringY) {
    return [];
  }
  const linesX = dataStringX.split("\n");
  const linesY = dataStringY.split("\n");
  let dataX = linesX.slice(1).map((line, index) => {
    const [frequencyX, realX, imagX] = line
      .split("\t")
      .slice(0, 3)
      .map((item) => Number(item.replace(",", ".")));
    const magnitudeX = calculateMagnitude(realX, imagX);
    return { frequency: frequencyX, [`${keyPrefix}magnitudeX`]: magnitudeX };
  });
  let dataY = linesY.slice(1).map((line, index) => {
    const [frequencyY, realY, imagY] = line
      .split("\t")
      .slice(0, 3)
      .map((item) => Number(item.replace(",", ".")));
    const magnitudeY = calculateMagnitude(realY, imagY);
    return { frequency: frequencyY, [`${keyPrefix}magnitudeY`]: magnitudeY };
  });

  let data = dataX.map((item, index) => {
    return {
      frequency: item.frequency,
      [`${keyPrefix}magnitudeX`]: item[`${keyPrefix}magnitudeX`],
      [`${keyPrefix}magnitudeY`]: dataY[index]
        ? dataY[index][`${keyPrefix}magnitudeY`]
        : null,
    };
  });

  data = data
    .filter(
      (item) =>
        !isNaN(item[`${keyPrefix}magnitudeX`]) ||
        !isNaN(item[`${keyPrefix}magnitudeY`])
    )
    .slice(200);

  return data;
};

const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${(
    "0" + d.getDate()
  ).slice(-2)}`;
};

const SpindleHealthMonitoring = () => {
  const [activeComponent, setActiveComponent] = useState(null);
  const [frfData1, setFrfData1] = useState(null);
  const [frfData2, setFrfData2] = useState(null);
  const [selectedRow1, setSelectedRow1] = useState(null);
  const [selectedRow2, setSelectedRow2] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [frfDatumIdToDelete, setFrfDatumIdToDelete] = useState(null);
  const [activeMagnitudes, setActiveMagnitudes] = useState(["magnitudeX"]);
  const [activeMagnitudesSecond, setActiveMagnitudesSecond] = useState([]);
  const [frfTableOffset, setFrfTableOffset] = useState(0);
  const [eventTableOffset, setEventTableOffset] = useState(0);

  const history = useHistory();
  const { machineId } = useParams();

  const [
    machineByMachineIdLoading,
    machineByMachineIdError,
    machineByMachineIdResponse,
  ] = useAPI(() => getMachineByMachineId({ machineId }));

  const [
    eventLogsByMachineIdLoading,
    eventLogsByMachineIdError,
    eventLogsByMachineIdResponse,
    fetchAllLogsByMachineId,
  ] = useAPI(
    () => getEventLogsByMachineId({ machineId, eventTableOffset }),
    [eventTableOffset]
  );

  const [
    maintenanceFrfDataByMachineIdLoading,
    maintenanceFrfDataByMachineIdError,
    maintenanceFrfDataByMachineIdResponse,
    fetchAllMaintenanceFrfDataByMachineId,
  ] = useAPI(
    () => getMaintenanceFrfDataByMachineId({ machineId, frfTableOffset }),
    [frfTableOffset]
  );

  const { machineDetails } = machineByMachineIdResponse;
  const { maintenanceFrfDataByMachineId } =
    maintenanceFrfDataByMachineIdResponse;
  const { eventLogs } = eventLogsByMachineIdResponse;

  const chartButtons = useMemo(() => {
    const buttons = [
      {
        label: "XX",
        className: `btn waves-effect ${
          activeMagnitudes.includes("magnitudeX")
            ? "btn active-button"
            : "btn not-active-button"
        }`,
        onClick: () => {
          setActiveMagnitudes((prev) =>
            prev.includes("magnitudeX")
              ? prev.filter((magnitude) => magnitude !== "magnitudeX")
              : [...prev, "magnitudeX"]
          );
        },
      },
      {
        label: "YY",
        className: `btn waves-effect mr-3 ${
          activeMagnitudes.includes("magnitudeY")
            ? "btn active-button"
            : "btn not-active-button"
        }`,
        onClick: () => {
          setActiveMagnitudes((prev) =>
            prev.includes("magnitudeY")
              ? prev.filter((magnitude) => magnitude !== "magnitudeY")
              : [...prev, "magnitudeY"]
          );
        },
      },
    ];
    if (frfData2) {
      buttons.push(
        {
          label: "XX",
          className: `btn waves-effect ${
            activeMagnitudesSecond.includes("magnitudeX")
              ? "btn active-button"
              : "btn not-active-button"
          }`,
          onClick: () => {
            setActiveMagnitudesSecond((prev) =>
              prev.includes("magnitudeX")
                ? prev.filter((magnitude) => magnitude !== "magnitudeX")
                : [...prev, "magnitudeX"]
            );
          },
        },
        {
          label: "YY",
          className: `btn waves-effect ${
            activeMagnitudesSecond.includes("magnitudeY")
              ? "btn active-button"
              : "btn not-active-button"
          }`,
          onClick: () => {
            setActiveMagnitudesSecond((prev) =>
              prev.includes("magnitudeY")
                ? prev.filter((magnitude) => magnitude !== "magnitudeY")
                : [...prev, "magnitudeY"]
            );
          },
        }
      );
    }

    return buttons;
  }, [activeMagnitudes, activeMagnitudesSecond, frfData2]);

  const componentMapping = useMemo(
    () => ({
      UploadFrfData: (
        <UploadFrfData
          fetchAllMaintenanceFrfDataByMachineId={
            fetchAllMaintenanceFrfDataByMachineId
          }
          setActiveComponent={setActiveComponent}
          machineId={machineId}
        />
      ),
      ReportAnEvent: (
        <ReportAnEvent
          setActiveComponent={setActiveComponent}
          fetchAllLogsByMachineId={fetchAllLogsByMachineId}
          machineId={machineId}
        />
      ),
      SelectModes: <SelectModes setActiveComponent={setActiveComponent} />,
    }),
    [fetchAllMaintenanceFrfDataByMachineId, fetchAllLogsByMachineId, machineId]
  );

  const handleFileLoad = useCallback((data, index) => {
    if (data.frfXxData && data.frfYyData) {
      if (index === 1) {
        setFrfData1({ frfXxData: data.frfXxData, frfYyData: data.frfYyData });
      } else if (index === 2) {
        setFrfData2({ frfXxData: data.frfXxData, frfYyData: data.frfYyData });
      }
    }
  }, []);

  const handleDeleteFrfDataByFrfDatumId = async (frfDatumId) => {
    try {
      if (frfDatumId.length <= 1) {
        notify("Machine should have at least 1 frf data. ❌");
      } else {
        await deleteMaintenanceFrfDataById({
          frfDatumId,
        });
        if (frfDatumId === selectedRow1) {
          setSelectedRow1(null);
          setFrfData1(null);
        } else if (frfDatumId === selectedRow2) {
          setSelectedRow2(null);
          setFrfData2(null);
        }
        notify("Frf Data Deleted Successfully. ✅ ");
        fetchAllMaintenanceFrfDataByMachineId();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      notify("An error occurred while deleting the data. ❌");
    }
  };

  const renderTableRows = (maintenanceFrfDataByMachineId) => {
    if (
      !maintenanceFrfDataByMachineId ||
      maintenanceFrfDataByMachineId.length === 0
    ) {
      return "";
    }
    return maintenanceFrfDataByMachineId.map((data) => (
      <tr
        key={data.frfDatumId}
        style={{
          cursor: "pointer",
          height: "10px",
          border:
            selectedRow1 === data.frfDatumId || selectedRow2 === data.frfDatumId
              ? "3px solid rgb(43, 142, 211)"
              : "",
        }}
        onClick={() => {
          if (selectedRow1 === data.frfDatumId) {
            setSelectedRow1(null);
            setFrfData1(null);
          } else if (selectedRow2 === data.frfDatumId) {
            setSelectedRow2(null);
            setFrfData2(null);
          } else if (!selectedRow1) {
            setSelectedRow1(data.frfDatumId);
          } else if (!selectedRow2) {
            setSelectedRow2(data.frfDatumId);
          }
        }}
      >
        {selectedRow1 === data.frfDatumId && (
          <GetFileAsJson
            frfXxUrl={data.frfXxUrl}
            frfYyUrl={data.frfYyUrl}
            onFileLoad={(data) => handleFileLoad(data, 1)}
          />
        )}
        {selectedRow2 === data.frfDatumId && (
          <GetFileAsJson
            frfXxUrl={data.frfXxUrl}
            frfYyUrl={data.frfYyUrl}
            onFileLoad={(data) => handleFileLoad(data, 2)}
          />
        )}
        <td className="text-center">{data.frfDatumId}</td>
        <td className="text-center">{data.createdAt}</td>
        <td className="text-center">{data.email}</td>
        <td className="text-center">{data.spindleHours}</td>
        <td className="text-center">
          <button
            className="btn btn-danger btn-sm"
            style={{ width: "7em" }}
            onClick={(event) => {
              event.stopPropagation();
              setShowDeleteDialog(true);
              setFrfDatumIdToDelete(data.frfDatumId);
            }}
          >
            Delete
            <i className="ion ion-md-trash ml-1" />
          </button>
        </td>
      </tr>
    ));
  };

  const renderEventsTable = () => {
    return eventLogs.map((data) => {
      const eventDate = formatDate(data.eventDate);
      const createdAt = formatDate(data.createdAt);

      return (
        <tr key={data.eventId} style={{ height: "10px" }}>
          <td className="text-center">{data.referenceNo}</td>
          <td className="text-center">{createdAt}</td>
          <td className="text-center">{eventDate}</td>
          <td className="text-center">{data.eventType}</td>
          <td className="text-center">{data.spindleHours}</td>
          <td className="d-flex justify-content-around text-center">
            {data.status}
          </td>
        </tr>
      );
    });
  };

  const combinedData = useMemo(() => {
    if (!frfData1 && !frfData2) return [];
    const data1 = frfData1
      ? parseData(frfData1.frfXxData, frfData1.frfYyData, "data1_")
      : [];
    const data2 = frfData2
      ? parseData(frfData2.frfXxData, frfData2.frfYyData, "data2_")
      : [];

    const combined = [...data1, ...data2];

    const uniqueFrequencies = [
      ...new Set(combined.map((item) => item.frequency)),
    ];

    return uniqueFrequencies.map((frequency) => {
      const data1Item =
        data1.find((item) => item.frequency === frequency) || {};
      const data2Item =
        data2.find((item) => item.frequency === frequency) || {};
      return {
        frequency,
        ...data1Item,
        ...data2Item,
      };
    });
  }, [frfData1, frfData2]);

  return (
    <div className="spindle-health-monitor-container mb-5">
      <div className="row main-container-class">
        <div className="d-flex flex-row">
          <div style={{ marginTop: "-5rem" }}>
            <button
              className="btn btn-primary waves-effect button back-to-shop-floor"
              onClick={() => history.goBack()}
            >
              Back to Shop Floor
            </button>
            <div className="ml-5 d-flex justify-content-around">
              <div>
                <h1 className="text-center">Spindle Health Monitoring</h1>
                <h3 className="text-center">
                  {!machineByMachineIdLoading && !machineByMachineIdError
                    ? `Machine Name: ${machineDetails?.machineName}`
                    : null}
                </h3>
                <h3 className="text-center">
                  {!machineByMachineIdLoading && !machineByMachineIdError
                    ? `Machine Model: ${machineDetails?.machineModel}`
                    : null}
                </h3>
              </div>
            </div>
            <LineChart
              width={800}
              height={500}
              data={combinedData}
              margin={{
                left: 45,
                bottom: 0,
              }}
            >
              <CartesianGrid strokeDasharray="6 6" />
              <XAxis
                dataKey="frequency"
                height={50}
                label={{
                  value: "Frequency (Hz)",
                  position: "right",
                  dx: -110,
                  dy: 8,
                }}
              />
              <YAxis
                width={60}
                label={{
                  value: "Magnitude (m/N)",
                  position: "insideTopLeft",
                  angle: -90,
                  dx: -35,
                  dy: 120,
                }}
              />
              <Tooltip />
              <Legend />
              {activeMagnitudes.includes("magnitudeX") && frfData1 && (
                <Line
                  type="monotone"
                  dataKey="data1_magnitudeX"
                  stroke="#8884d8"
                  dot={false}
                  activeDot={false}
                />
              )}
              {activeMagnitudes.includes("magnitudeY") && frfData1 && (
                <Line
                  type="monotone"
                  dataKey="data1_magnitudeY"
                  stroke="#82ca9d"
                  dot={false}
                  activeDot={false}
                />
              )}
              {activeMagnitudesSecond.includes("magnitudeX") && frfData2 && (
                <Line
                  type="monotone"
                  dataKey="data2_magnitudeX"
                  stroke="#FF0000"
                  dot={false}
                  activeDot={false}
                />
              )}
              {activeMagnitudesSecond.includes("magnitudeY") && frfData2 && (
                <Line
                  type="monotone"
                  dataKey="data2_magnitudeY"
                  stroke="#00FF00"
                  dot={false}
                  activeDot={false}
                />
              )}
            </LineChart>
            <div className="d-flex flex-row justify-content-center align-items-center mt-2 ml-3">
              {chartButtons.map((button, index) => (
                <button
                  key={index}
                  className={button.className}
                  onClick={button.onClick}
                >
                  {button.label}
                </button>
              ))}
            </div>
            <div
              className="table-responsive mb-5 ml-5"
              style={{ marginTop: "-6rem" }}
            >
              <div className="d-flex justify-content-end mb-2">
                <div></div>
                <div>
                  <button
                    className="btn btn-primary waves-effect button"
                    onClick={() => setActiveComponent("ReportAnEvent")}
                  >
                    Report an Event
                  </button>
                </div>
              </div>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th className="text-center">Reference No</th>
                    <th className="text-center">Submission Date</th>
                    <th className="text-center">Event Date</th>
                    <th className="text-center">Event Type</th>
                    <th className="text-center">Spindle Hours</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {eventLogsByMachineIdError ? (
                    <tr>
                      <td colSpan="3">Error Loading Event data</td>
                    </tr>
                  ) : eventLogsByMachineIdLoading ? (
                    <tr>
                      <td colSpan="3">Loading Event Data...</td>
                    </tr>
                  ) : (
                    renderEventsTable(eventLogs)
                  )}
                </tbody>
                <div
                  className="dataTables_paginate paging_simple_numbers"
                  id="datatable_paginate"
                >
                  <ul className="pagination">
                    <li
                      className={
                        eventTableOffset === 0
                          ? "paginate_button page-item previous disabled"
                          : null
                      }
                      id="datatable_previous"
                    >
                      <button
                        type="button"
                        aria-controls="datatable"
                        className="page-link"
                        disabled={eventTableOffset === 0 ? true : false}
                        onClick={() =>
                          setEventTableOffset(eventTableOffset - 6)
                        }
                      >
                        Prev Page
                      </button>
                    </li>
                    <li
                      className={
                        eventLogs?.length < 6
                          ? "paginate_button page-item previous disabled"
                          : "none"
                      }
                      id="datatable_next"
                    >
                      <button
                        type="button"
                        aria-controls="datatable"
                        className="page-link"
                        disabled={eventLogs?.length < 6 ? true : false}
                        onClick={() =>
                          setEventTableOffset(eventTableOffset + 6)
                        }
                      >
                        Next Page
                      </button>
                    </li>
                  </ul>
                </div>
              </table>
            </div>
          </div>
          <div className="table-responsive ml-3">
            <div className="d-flex justify-content-end mb-2">
              <div></div>
              <div>
                <button
                  className="btn btn-primary waves-effect button"
                  style={{ width: "auto" }}
                  onClick={() => setActiveComponent("UploadFrfData")}
                >
                  Upload New Data
                </button>
              </div>
            </div>
            <table
              id="datatable"
              className="table table-bordered text-center"
              role="grid"
              aria-describedby="datatable_info"
            >
              <thead>
                <tr>
                  <th className="text-center">Frf Datum Id</th>
                  <th className="text-center">Upload Date</th>
                  <th>Created By</th>
                  <th>Spindle Hours</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {maintenanceFrfDataByMachineIdError ? (
                  <tr>
                    <td colSpan="3">Error Loading FRF data</td>
                  </tr>
                ) : maintenanceFrfDataByMachineIdLoading ? (
                  <tr>
                    <td colSpan="3">Loading FRF Data...</td>
                  </tr>
                ) : (
                  renderTableRows(
                    maintenanceFrfDataByMachineId,
                    handleDeleteFrfDataByFrfDatumId
                  )
                )}
              </tbody>
              <div
                className="dataTables_paginate paging_simple_numbers"
                id="datatable_paginate"
              >
                <ul className="pagination">
                  <li
                    className={
                      frfTableOffset === 0
                        ? "paginate_button page-item previous disabled"
                        : null
                    }
                    id="datatable_previous"
                  >
                    <button
                      type="button"
                      aria-controls="datatable"
                      className="page-link"
                      disabled={frfTableOffset === 0 ? true : false}
                      onClick={() => setFrfTableOffset(frfTableOffset - 6)}
                    >
                      Prev Page
                    </button>
                  </li>
                  <li
                    className={
                      maintenanceFrfDataByMachineId?.length < 6
                        ? "paginate_button page-item previous disabled"
                        : "none"
                    }
                    id="datatable_next"
                  >
                    <button
                      type="button"
                      aria-controls="datatable"
                      className="page-link"
                      disabled={
                        maintenanceFrfDataByMachineId?.length < 6 ? true : false
                      }
                      onClick={() => setFrfTableOffset(frfTableOffset + 6)}
                    >
                      Next Page
                    </button>
                  </li>
                </ul>
              </div>
            </table>
          </div>
        </div>
      </div>
      {componentMapping[activeComponent]}
      {showDeleteDialog && (
        <DeleteFrfRecordById
          handleDeleteFrfDataByFrfDatumId={handleDeleteFrfDataByFrfDatumId}
          setShowDeleteDialog={setShowDeleteDialog}
          frfDatumIdToDelete={frfDatumIdToDelete}
          selectedRow={selectedRow1 || selectedRow2}
        />
      )}
    </div>
  );
};

export default SpindleHealthMonitoring;
